/* @flow */

import type { ShakaTrack } from '../../components/player/implementation/shakaTypes';
import type { VideoPlayerMediaInfo } from '../../components/player/implementation/types';

import Localizer from '../localization';
import { LANGUAGE } from './metadata/Types';

const HEARING_IMPAIRED_CODE = 'HI';
const CAPTION = 'caption';
const HARD_OF_HEARING = 'hard of hearing';

export const CAPTIONS = 'captions';
export const SUBTITLES = 'subtitles';

// Parameter 'code' can be any Unicode language Id (see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames/of#code)
const convertToHumanReadable = (code: string): string => {
  // Special case
  const ucCode = code.toUpperCase();
  if (ucCode === LANGUAGE.OriginalVersion) {
    return Localizer.localize('player.language.qaa');
  }
  if (ucCode === LANGUAGE.VisuallyImpaired || ucCode === LANGUAGE.VisuallyImpairedAlt) {
    return Localizer.localize('player.language.qad');
  }

  // General case
  try {
    const languageNames = new Intl.DisplayNames([Localizer.language], { type: 'language' });
    return languageNames.of(code);
  } catch {
    return code;
  }
};

const isCaptionInArray = (array?: Array<string>): boolean => {
  if (!array) {
    return false;
  }

  return array.some((element) => element.toLowerCase().indexOf(CAPTION) > -1);
};

const isHearingImpairedSubtitlesTrack = (track: VideoPlayerMediaInfo | ShakaTrack | null): boolean => {
  if (!track) {
    return false;
  }

  const { accessibility, accessibilityPurpose, kind, roles } = track;
  return (accessibilityPurpose?.toLowerCase() === HARD_OF_HEARING || isCaptionInArray(accessibility) || isCaptionInArray(roles) || kind?.toLowerCase() === CAPTIONS) ?? false;
};

const isVisuallyImpairedAudioTrack = (track: VideoPlayerMediaInfo | null): boolean => {
  if (!track) {
    return false;
  }

  const { lang } = track;
  const ucLang = lang.toUpperCase();
  return ucLang === LANGUAGE.VisuallyImpaired || ucLang === LANGUAGE.VisuallyImpairedAlt;
};

const getAudioSettingCode = (track: VideoPlayerMediaInfo): string => {
  const { lang } = track;

  return lang;
};

const getSubtitlesSettingCode = (track: VideoPlayerMediaInfo | null): string => {
  if (!track) {
    return '';
  }

  const { lang } = track;

  if (isHearingImpairedSubtitlesTrack(track)) {
    return `${lang}:${HEARING_IMPAIRED_CODE}`;
  }

  return lang;
};

/*
 * Find and return the track matching the given language code or the closest one, e.g.:
 *  - languageCode=fr    -> fr track > fr hearing impaired track > null
 *  - languageCode=fr:HI -> fr hearing impaired track > fr track > null
 */
const getSubtitlesTrackFromCode = (tracks: Array<VideoPlayerMediaInfo>, languageCode: string): VideoPlayerMediaInfo | null => {
  if (tracks.length === 0) {
    return null;
  }

  const [language, isHearingImpaired] = languageCode.split(':');

  const hearingImpairedFunc = isHearingImpaired ? (track: VideoPlayerMediaInfo) => isHearingImpairedSubtitlesTrack(track) : (track) => !isHearingImpairedSubtitlesTrack(track);
  const requestedLanguageTracks = tracks.filter((track) => track.lang === language);

  const exactRequestedLanguage = requestedLanguageTracks.find((track) => hearingImpairedFunc(track));
  if (exactRequestedLanguage) {
    // Exact language has been found (e.g. "fr" or "fr hearing impaired")
    return exactRequestedLanguage;
  }

  const closestRequestedLanguage = requestedLanguageTracks.find((track) => !hearingImpairedFunc(track));
  if (closestRequestedLanguage) {
    // Closest language has been found (e.g. "fr" for "fr hearing impaired" or "fr hearing impaired" for "fr")
    return closestRequestedLanguage;
  }

  // No matching track was found
  return null;
};

export { convertToHumanReadable, getAudioSettingCode, getSubtitlesSettingCode, getSubtitlesTrackFromCode, isHearingImpairedSubtitlesTrack, isVisuallyImpairedAudioTrack };
